import { useTranslation } from "react-i18next";
import { useToastMessage } from "../hooks/useToastMessage";
import { useModifiedTranslation } from "../hooks/useModifiedTranslation";
import {
  AccessToken,
  LoginErrorCode,
  isObject,
  isValidAppApiError,
  AuthData,
  LoginMethod,
} from "../types";
import { contractEnginesAtom, outputStenScoreAtom } from "../store";
import { useCallback } from "react";
import { useAtom } from "jotai";
import { voislogApi } from "./apiClients/voislogApi";
import { ApiError } from "./apiClients/ApiError";
import { decodeJwt } from "./decodeJwt";
import dayjs from "dayjs";

function expToExpiredAt(access_token: string): string {
  const decoded = decodeJwt(access_token);
  return dayjs.unix(decoded.exp ?? 0).toISOString();
}

function getLoginErrorCode(method: LoginMethod): LoginErrorCode {
  switch (method) {
    case LoginMethod.LoginForm:
      return "INVALID_TOKEN";

    case LoginMethod.GoogleAuth:
      return "INVALID_GOOGLE_TOKEN";

    case LoginMethod.TwoocaAuth:
      return "INVALID_TWOOCA_TOKEN";
  }
}

// クライアントアプリのバージョンが古い(426)場合、案内後にページを再読み込みする
function handleUpgradeRequired(e: unknown, message: string): boolean {
  if (!(e instanceof ApiError) || e.status !== 426) return false;
  window.alert(message);
  window.location.reload();
  return true;
}

type GetAccessToken = <T extends LoginMethod>(
  method: T,
  onInvalidUserInfo: (errorCode: LoginErrorCode) => void,
  data: AuthData[T],
) => Promise<AccessToken | false>;

export function useGetAccessToken(): GetAccessToken {
  const toastMessage = useToastMessage();
  const { i18n } = useTranslation();
  const t = useModifiedTranslation();
  // STENスコアの出力有無のグローバル状態管理用
  const [, setOutputStenScore] = useAtom(outputStenScoreAtom);
  // 契約エンジン種別のグローバル状態管理用
  const [, setContractEngines] = useAtom(contractEnginesAtom);

  const requestAuthToken = async (
    authMethod: LoginMethod,
    authData: unknown,
  ): Promise<Omit<AccessToken, "user_id" | "company_id">> => {
    switch (authMethod) {
      case LoginMethod.LoginForm: {
        const data = authData as AuthData[LoginMethod.LoginForm];
        const res = await voislogApi.POST("/v2/core/auth/token", {
          body: { email: data.email, password: data.password },
        });
        const resData = res.data!;
        return {
          access_token: resData.access_token,
          expired_at: expToExpiredAt(resData.access_token),
        };
      }

      case LoginMethod.GoogleAuth: {
        const data = authData as AuthData[LoginMethod.GoogleAuth];
        const res = await voislogApi.POST("/v2/core/auth/google/token", {
          body: { credential: data.credential },
        });
        const resData = res.data!;
        return {
          access_token: resData.access_token,
          expired_at: expToExpiredAt(resData.access_token),
        };
      }

      // NOTE: VOISLOG未実装
      // case LoginMethod.TwoocaAuth: {
      //   const data = authData as AuthData[LoginMethod.TwoocaAuth];
      //   const response = await appAxios.post("/auth/twooca/token", data);
      //   const access_token = response.data.access_token as string;
      //   return { access_token, expired_at: expToExpiredAt(access_token) };
      // }

      case LoginMethod.TwoocaAuth:
        throw new Error("TwoocaAuth is not implemented");
    }
  };

  const handleLoginError = useCallback(
    (
      e: unknown,
      method: LoginMethod,
      onInvalidUserInfo: (errorCode: LoginErrorCode) => void,
      authData: unknown,
    ): void => {
      const body = e instanceof ApiError ? e.body : e;

      if (
        !isValidAppApiError(body) ||
        !isObject((body as { detail?: unknown }).detail)
      ) {
        toastMessage(e);
        return;
      }

      const errDetail = (body as { detail: { type: string } }).detail;
      if (errDetail.type === "auth_error.invalid_input") {
        onInvalidUserInfo(getLoginErrorCode(method));
      } else if (
        method == LoginMethod.LoginForm &&
        errDetail.type === "auth_error.locked_temporary"
      ) {
        const data = authData as AuthData[LoginMethod.LoginForm];
        toastMessage(e);
        voislogApi
          .POST("/v2/core/auth/unlock/request", {
            body: {
              email: data.email,
              app_type: "vAnalysis",
              language: i18n.resolvedLanguage as "ja" | "en",
            },
          })
          .catch((e) => {
            console.error("Unlock to Temporary Locked Error!", e);
            toastMessage(e);
          });
      } else {
        toastMessage(e);
      }
    },
    [i18n.resolvedLanguage, toastMessage],
  );

  return useCallback(
    async (method, onInvalidUserInfo, authData) => {
      try {
        const tokenData = await requestAuthToken(method, authData);

        // STENスコアの出力有無の判定と状態保持
        const { data: me } = await voislogApi.GET(
          "/v2/core/users/{user_id_or_me}",
          {
            params: { path: { user_id_or_me: "me" } },
            headers: { Authorization: `Bearer ${tokenData.access_token}` },
          },
        );
        const user_id = me?.id;
        const company_id = me?.companies?.find((c) => c.is_member)?.id;
        if (!user_id || !company_id) {
          onInvalidUserInfo("ENGINE_DISABLED");
          return false;
        }
        const { data: contracts } = await voislogApi.GET(
          "/v2/core/company-contracts",
          {
            params: {
              query: {
                app: "vAnalysis",
                engine: "mi1",
                company_id,
                valid_today: true,
              },
            },
            headers: { Authorization: `Bearer ${tokenData.access_token}` },
          },
        );
        if (!contracts || contracts.length === 0) {
          onInvalidUserInfo("ENGINE_DISABLED");
          return false;
        }

        const outputStenScore = contracts.some(
          (c) => c.contract_plan?.engine_options?.output_sten_score === true,
        );
        setOutputStenScore(outputStenScore);
        setContractEngines(contracts.map((c) => c.contract_plan.engine));

        return { ...tokenData, user_id, company_id };
      } catch (e) {
        console.error("Get Access Token Error!", e);

        if (handleUpgradeRequired(e, t("Error.loginError.upgrade_required")))
          return false;

        handleLoginError(e, method, onInvalidUserInfo, authData);
        return false;
      }
    },
    [t, handleLoginError, setOutputStenScore, setContractEngines],
  );
}
